import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Course, CourseUser, IntegrationStatus, Provider } from 'graphqlAPI'
import StateMonitor from 'components/ui/state-monitor/StateMonitor'
import UserAvatar from 'components/ui/user-avatar/UserAvatar'
import { createRouteMonitoringParticipant } from 'enum/routes'
import { MonitoringStatus } from 'enum'
import CustomLink from 'components/ui/CustomLink'
import {
  EnrolState,
  FINAL_STATE_MAP_EVALUATION_STATE,
} from 'enum/enrol-state.enum'
import { CourseEvaluationState } from 'enum/courseEvaluation-state.enum'
import FinalState from 'components/synchronousCourses/FinalState'
import Button, { ButtonColors, ButtonSizes } from 'design-system/button'
import coursera from 'assets/courseraIndicator.svg'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import { useIntegrationStatus } from 'hooks/useIntegrationStatus'
import { Evaluations } from '../../components/Evaluations'
import Options from '../Options'
import { prefix } from '../../constants'
import { Column } from './types'
import styles from './useColumns.module.scss'

const prefixHeaders = `${prefix}.headers`
const prefixColumns = `${prefix}.columns`

interface useColumnsProps {
  evaluationsCount: number
  course?: Course
  setSelected: React.Dispatch<React.SetStateAction<CourseUser | null>>
}

const ICONS_BY_PROVIDER: {
  [P in Provider]?: string
} = {
  [Provider.Coursera]: coursera,
}

const INTEGRATIONS_KEYS = {
  [Provider.Coursera]: 'configuration.integrations.coursera',
}

export function useColumns({
  evaluationsCount,
  course,
  setSelected,
}: useColumnsProps) {
  const { t } = useTranslation()

  const { lessonsCount = 0, integrationCourse } = { ...course }

  const isSynchronous = !!course?.synchronousCourse

  const integrationStatusTranslations = useIntegrationStatus()

  const columns: Column[] = useMemo(() => {
    const provider = integrationCourse?.provider
    const icon = provider && ICONS_BY_PROVIDER[provider]
    const providerLabel = provider && t(INTEGRATIONS_KEYS[provider])

    const columnsBase: (Column | null | false)[] = [
      {
        title: t(`${prefixHeaders}.name`),
        sorteable: true,
        key: 'user_name',
        hideable: false,
        width: 70,
        render: (
          _,
          {
            user: { name, avatar, lastname, position, roleId },
            courseId,
            userId,
            advance = MonitoringStatus.Pending,
          }: CourseUser
        ) => {
          const disabled = advance === MonitoringStatus.Pending
          return (
            <CustomLink
              to={createRouteMonitoringParticipant({
                participantId: `${userId}`,
                courseId,
              })}
              disabled={disabled}
              className={styles.link}
            >
              <UserAvatar
                user={{
                  avatar,
                  name,
                  lastname,
                  position,
                  roleId,
                }}
                disabled={disabled}
              />
            </CustomLink>
          )
        },
      },
      {
        title: t(`${prefixHeaders}.area`),
        key: 'area_name',
        width: 100,
        sorteable: true,
        render: (_, { area }: CourseUser) => (
          <span className="text-wrap">{area?.name}</span>
        ),
      },
      !!provider && {
        title: (
          <div className={styles.integration}>
            {t(`${prefixHeaders}.integrationStatus`)}
            <CommonToolTip text={providerLabel} position="bottom">
              {icon && (
                <img src={icon} alt={provider} className={styles.icon} />
              )}
            </CommonToolTip>
          </div>
        ),
        key: 'enrol_integration_status',
        width: 150,
        sorteable: true,
        render: (_, { enrol }) => {
          const key = enrol?.integrationStatus || IntegrationStatus.Pending
          return <div>{integrationStatusTranslations[key]}</div>
        },
      },
      {
        title: t(`${prefixHeaders}.state`),
        key: 'advance',
        width: 150,
        sorteable: true,
        render: (_, { advance }) => <StateMonitor state={advance || 0} />,
      },
      {
        title: t(`${prefixHeaders}.progress`),
        key: 'enrol_overall_progress',
        width: 170,
        sorteable: true,
        render: (_, { overallProgress }) => (
          <>{(overallProgress || 0).toFixed(1)}%</>
        ),
      },
      isSynchronous && {
        title: t(`${prefixHeaders}.finalState`),
        key: 'synchronous_course_enrols_final_state',
        width: 120,
        sorteable: true,
        render: (_, { synchronousCourseEnrol }: CourseUser) => {
          const { finalState = EnrolState.Pending } = {
            ...synchronousCourseEnrol,
          }
          return (
            <div className="text-center">
              <FinalState state={finalState as EnrolState} />
            </div>
          )
        },
      },
      !isSynchronous && {
        title: t(`${prefixHeaders}.evaluations`),
        key: 'state_evaluations',
        width: 120,
        sorteable: true,
        render: (_state, courseUser: CourseUser) => {
          const synchronousCourseEvaluationsCount =
            course?.synchronousCourse?.evaluationsCount
          const finalState =
            isSynchronous &&
            (courseUser.synchronousCourseEnrol?.finalState as EnrolState)

          return (
            ((evaluationsCount || synchronousCourseEvaluationsCount) && (
              <Evaluations
                state={
                  finalState
                    ? FINAL_STATE_MAP_EVALUATION_STATE[finalState] ||
                      CourseEvaluationState.inProcess
                    : courseUser.evaluationsState
                }
                courseUser={courseUser}
                course={course}
              />
            )) || <div className="text-center">-</div>
          )
        },
      },
      ...(isSynchronous
        ? new Array(course?.synchronousCourseEvaluationsCount || 0).fill(0).map(
            (_, index): Column => ({
              title: (
                <div className="text-wrap">
                  {t(`${prefixHeaders}.synchronousCourseEvaluation`, {
                    index: index + 1,
                  })}
                </div>
              ),
              key: `synchronous_course_evaluations_${index}`,
              width: 120,
              sorteable: false,
              render: (_, { synchronousCourseEvaluations }: CourseUser) => {
                const evaluation = synchronousCourseEvaluations?.[index]
                return (
                  <div className="text-center">
                    {evaluation?.qualification || '-'}
                  </div>
                )
              },
            })
          )
        : []),
      isSynchronous && {
        title: t(`${prefixHeaders}.attendance`),
        key: 'synchronous_course_enrol_attendance',
        width: 120,
        sorteable: true,
        render: (_, { synchronousCourseEnrol }: CourseUser) => (
          <div className="text-center">
            {synchronousCourseEnrol?.attendance || 0}%
          </div>
        ),
      },
      lessonsCount > 0 && {
        title: t(`${prefixHeaders}.lessons`),
        key: 'assignedLessons',
        width: 120,
        sorteable: true,
        render: (_, { assignedLessons }) => (
          <span>
            {t(`${prefixColumns}.lessons`, {
              count: assignedLessons || 0,
              total: lessonsCount,
            })}
          </span>
        ),
      },
      {
        title: t(`${prefixHeaders}.actions`),
        key: 'action',
        width: 100,
        hideable: false,
        render: (id, courseUser: CourseUser) => {
          const { finalState = EnrolState.Pending } = {
            ...courseUser.synchronousCourseEnrol,
          }
          const canEdit = isSynchronous && finalState === EnrolState.Pending
          return (
            <div className="d-flex align-items-center justify-content-end">
              {canEdit && (
                <Button
                  color={ButtonColors.RedDarken}
                  size={ButtonSizes.Small}
                  onClick={() => setSelected(courseUser)}
                >
                  {t('administration.monitor.buttons.finish')}
                </Button>
              )}
              <Options courseUser={courseUser} />
            </div>
          )
        },
      },
    ]
    return columnsBase.reduce<Column[]>((acc, column) => {
      if (column) acc.push(column)
      return acc
    }, [])
  }, [
    course,
    evaluationsCount,
    integrationCourse,
    integrationStatusTranslations,
    isSynchronous,
    lessonsCount,
    setSelected,
    t,
  ])
  return columns
}
